import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { Button, Checkbox, Label } from "@tellescope/design-system";
import type { HideUnsubmittedAfter, HideUnsubmittedFormRule } from "../../../../../../shared/schema";
import { HideAfterInput } from "./HideAfterInput";
import { HideUnsubmittedFormRuleRow } from "./HideUnsubmittedFormRuleRow";
import { useHideUnsubmittedSection, type HideUnsubmittedSettings } from "./useHideUnsubmittedSection";

interface HideUnsubmittedSectionProps {
  hideAfter: HideUnsubmittedAfter | undefined;
  rules: HideUnsubmittedFormRule[] | undefined;
  onChange: (updates: HideUnsubmittedSettings) => void;
}

export const HideUnsubmittedSection = ({ hideAfter, rules = [], onChange }: HideUnsubmittedSectionProps) => {
  const { t } = useTranslation();
  const checkboxId = useId();
  const { handleDefaultEnabledChange, handleDefaultChange, addRule, updateRule, removeRule, formIdsInOtherRules } =
    useHideUnsubmittedSection(rules, onChange);

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Checkbox id={checkboxId} checked={!!hideAfter} onCheckedChange={handleDefaultEnabledChange} />
          <Label htmlFor={checkboxId} className="cursor-pointer">
            {t("Hide unsubmitted forms after a period of time")}
          </Label>
        </div>
        {hideAfter && (
          <div className="pl-6">
            <HideAfterInput value={hideAfter} onChange={handleDefaultChange} />
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label>{t("Rules for specific forms")}</Label>
        <p className="text-xs text-muted-foreground">
          {t("A rule replaces the setting above for the forms it includes.")}
        </p>
        {rules.map((rule) => (
          <HideUnsubmittedFormRuleRow
            key={rule.id}
            rule={rule}
            formIdsInOtherRules={formIdsInOtherRules(rule.id)}
            onChange={updateRule}
            onRemove={() => removeRule(rule.id)}
          />
        ))}
        <Button variant="outline" size="sm" onClick={addRule} className="w-full">
          <Plus className="h-4 w-4 mr-1" />
          {t("Add rule for specific forms")}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        {t("Submitted forms are always shown. The time is counted from when the form was sent.")}
      </p>
    </div>
  );
};
